import { Button, ButtonWithLoader } from '@neo/primitives/button'
import { ChevronLeft, Sparkles, X } from 'lucide-react'
import { useState } from 'react'
import type {
  RAgentJudge,
  RJudgeTestResponse,
  RJudgeTestResult,
} from 'v2/types/api/agent-observability'
import { successToast } from 'v2/utils/toasts'
import { useCalibrateMetricMutation } from './metrics-tab.queries'
import { notScoredBadge, oppositeVerdict, RunLink, verdictBadge } from './metrics-test-shared'

interface TestResultsProps {
  judge: RAgentJudge
  flowId: string
  data: RJudgeTestResponse
  onBack: () => void
  onCalibrated: (judgeId: string, description: string) => void
  onDone: () => void
}

// A flag marks the CORRECT verdict for a call the judge got wrong. Several
// flags are collected, then one "Revamp metric" call rewrites the description
// against all of them at once.
type Flags = Record<string, 'pass' | 'fail'>

export const TestResults = ({
  judge,
  flowId,
  data,
  onBack,
  onCalibrated,
  onDone,
}: TestResultsProps) => {
  const [flagChoiceFor, setFlagChoiceFor] = useState<string | null>(null)
  const [flags, setFlags] = useState<Flags>({})
  const calibrateMutation = useCalibrateMetricMutation()

  const setFlag = (sessionId: string, desired: 'pass' | 'fail') => {
    setFlagChoiceFor(null)
    setFlags((prev) => ({ ...prev, [sessionId]: desired }))
  }
  const removeFlag = (sessionId: string) =>
    setFlags((prev) => {
      const next = { ...prev }
      delete next[sessionId]
      return next
    })

  const flaggedCount = Object.keys(flags).length

  const revamp = () => {
    calibrateMutation.mutate(
      {
        description: judge.description,
        scope: judge.scope,
        examples: Object.entries(flags).map(([session_id, desired_verdict]) => ({
          session_id,
          desired_verdict,
        })),
      },
      {
        onSuccess: (res) => {
          onCalibrated(judge.id, res.description)
          successToast({ title: 'Metric revamped — review & save' })
          onDone()
        },
      },
    )
  }

  const onFlagClick = (result: RJudgeTestResult) => {
    // A call AO could not judge has no verdict to correct; letting it be flagged would feed
    // an unscored call into calibration as a labelled example.
    if (!result.available) return
    if (result.verdict === 'pass' || result.verdict === 'fail') {
      setFlag(result.session_id, oppositeVerdict(result.verdict) as 'pass' | 'fail')
    } else {
      setFlagChoiceFor(result.session_id)
    }
  }

  return (
    <div className="flex flex-col gap-3" data-testid="list-test-results">
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-1 text-s-400 text-primary"
          onClick={onBack}
          data-testid="btn-change-selection"
        >
          <ChevronLeft size={14} /> Change selection
        </button>
        <span className="text-s-400 text-tertiary" data-testid="text-test-summary">
          {data.summary.scored} scored · {data.summary.failed} failed · {data.summary.unknown}{' '}
          unknown
        </span>
      </div>

      {flaggedCount > 0 && (
        <div
          className="flex items-center justify-between rounded-lg border border-st2 bg-bg2 px-3 py-2.5"
          data-testid="revamp-bar"
        >
          <span className="text-s-400 text-secondary">
            {flaggedCount} verdict{flaggedCount > 1 ? 's' : ''} flagged as wrong
          </span>
          <ButtonWithLoader
            size="sm"
            loading={calibrateMutation.isPending}
            onClick={revamp}
            className="gap-1.5"
            data-testid="btn-revamp-metric"
          >
            <Sparkles size={14} />
            Revamp metric
          </ButtonWithLoader>
        </div>
      )}

      {data.results.map((result: RJudgeTestResult) => {
        const flagged = flags[result.session_id]
        return (
          <div
            key={result.session_id}
            className={`rounded-lg p-3 flex flex-col gap-2 border ${flagged ? 'border-primary' : 'border-st2'}`}
            data-testid={`row-test-result-${result.session_id}`}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2">
                {result.available ? verdictBadge(result.verdict) : notScoredBadge()}
                <RunLink
                  flowId={flowId}
                  sessionId={result.session_id}
                  runId={result.flow_run_uuid}
                />
              </span>
              {!result.available ? null : flagged ? (
                <span className="flex items-center gap-1.5 text-s-400 text-secondary">
                  Should be {flagged === 'pass' ? 'Pass' : 'Fail'}
                  <button
                    type="button"
                    className="text-tertiary hover:text-primary"
                    onClick={() => removeFlag(result.session_id)}
                    aria-label="Remove flag"
                    data-testid={`btn-unflag-${result.session_id}`}
                  >
                    <X size={14} />
                  </button>
                </span>
              ) : flagChoiceFor === result.session_id ? (
                <span className="flex items-center gap-2 text-s-400">
                  <span className="text-tertiary">Correct:</span>
                  <button
                    type="button"
                    className="text-status-success"
                    onClick={() => setFlag(result.session_id, 'pass')}
                    data-testid={`btn-flag-pass-${result.session_id}`}
                  >
                    Pass
                  </button>
                  <button
                    type="button"
                    className="text-destructive"
                    onClick={() => setFlag(result.session_id, 'fail')}
                    data-testid={`btn-flag-fail-${result.session_id}`}
                  >
                    Fail
                  </button>
                </span>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onFlagClick(result)}
                  data-testid={`btn-flag-${result.session_id}`}
                >
                  Flag
                </Button>
              )}
            </div>
            <p className="text-s-400 text-secondary">{result.reason}</p>
            {!result.available && result.technical_reason && (
              <p
                className="text-xs-400 text-tertiary"
                data-testid={`text-not-scored-${result.session_id}`}
              >
                {result.technical_reason}
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}
